/**
 * Recurring-session edit: choose this occurrence only vs this and following.
 */
import * as React from 'react';
import { ConfirmModal } from '@/components/common/modals/ConfirmModal';
import { RadioGroup } from '@/components/forms/RadioGroup';
import { SESSION_RECURRENCE_SCOPE_EDIT_OPTIONS } from '@/components/sessions/session-configs';
import type { RecurrenceUpdateScope } from '@/types/inperson/session.types';

/** Props for {@link UpdateRecurrenceScopeModal}. */
interface UpdateRecurrenceScopeModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (scope: RecurrenceUpdateScope) => void | Promise<void>;
  isSubmitting?: boolean;
}

/** True when `value` is a PATCH recurrence scope. */
function isRecurrenceUpdateScope(value: string): value is RecurrenceUpdateScope {
  return value === 'occurrence' || value === 'following';
}

/** Confirm dialog: occurrence-only or this occurrence and following ones. */
export const UpdateRecurrenceScopeModal: React.FC<Readonly<UpdateRecurrenceScopeModalProps>> = ({
  open,
  onClose,
  onConfirm,
  isSubmitting = false,
}) => {
  const [scope, setScope] = React.useState<RecurrenceUpdateScope | ''>('');

  React.useEffect(() => {
    if (!open) return;
    setScope('');
  }, [open]);

  function handleScopeChange(value: string): void {
    if (!isRecurrenceUpdateScope(value)) return;
    setScope(value);
  }

  async function handleConfirm(): Promise<void> {
    if (!scope) return;
    await onConfirm(scope);
  }

  return (
    <ConfirmModal
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      title="Portée de la modification"
      description="Cette séance est récurrente. Vous devez choisir si les modifications s'appliquent uniquement à cette occurrence, ou à celle-ci et aux séances futures."
      confirmText="Confirmer"
      variant="primary"
      confirmDisabled={!scope}
      isLoading={isSubmitting}
    >
      <RadioGroup
        label="Choix obligatoire"
        required
        name="recurrence-update-scope"
        options={SESSION_RECURRENCE_SCOPE_EDIT_OPTIONS}
        value={scope}
        onValueChange={handleScopeChange}
        disabled={isSubmitting}
      />
    </ConfirmModal>
  );
};
